import axios from 'axios';
import { ACTION_TYPE, API_EXCEPTION_MSG, COMPANY_CFC } from '../../Common';
import { CommonRequest, CommonResult, ConditionItem, isAxiosError, OK } from '../Common';
import { NumberOrNull } from '../../types';

const endpoint = process.env.REACT_APP_API_URL;
const URL = `${endpoint}/customerList`;

export type PagingRequest = {
  maxItemPerPage: number;
  from: number;
};

export type SortRequest = {
  col: string;
  order: 'asc' | 'desc';
};

export interface CustomerListApiRequest extends CommonRequest {
  /**
   * マスキングフラグ
   * 0：クルー 1：代理店・営業
   */
  maskingFlg: string;
  /**
   * ページング情報
   */
  paging: PagingRequest;
  /**
   * ソート情報
   */
  sort: SortRequest;

  /**
   * 絞り込み条件
   */
  searchBranch: ConditionItem[];
}

/**
 * 顧客一覧に表示する商談レベル情報
 */
export interface NegotiationInfoOnCustomerInfoList {
  /**
   * 商談名
   */
  negotiationName: string;
  /**
   * 臨時コード1
   */
  tempCd1: string;
  /**
   * 臨時コード2
   */
  tempCd2: string;
  /**
   * 臨時コード3
   */
  tempCd3: string;
  /**
   * 臨時コード4
   */
  tempCd4: string;
}

/**
 * 顧客一覧に表示する商品レベルの情報
 */
export interface ProductInfoOnCustomerInfo {
  /**
   * スマホ 純新規 件数
   */
  smartphoneNew: number;
  /**
   * スマホ 機種変更 件数
   */
  smartphoneChange: number;
  /**
   * スマホ 乗換(転入) 件数
   */
  smartphoneMnp: number;
  /**
   * スマホ 番号移行 件数
   */
  smartphoneTransfer: number;
  /**
   * SB光 件数(合計)
   */
  sbHikari: number;
  /**
   * SBAir 件数(合計)
   */
  sbAir: number;
  /**
   * SB光＋ 件数(合計)
   */
  aurora: number;
}

/**
 * 顧客一覧に表示するアクションレベルの情報
 */
export interface ActionInfoOnCustomerInfo {
  /**
   * アクションタイプ
   */
  actionType: ACTION_TYPE;
  /**
   * アクション名
   */
  actionName: string;
  /**
   * 曜日区分
   */
  dayType: string;
  actionDate: string | null;
  actionTimeType: string;
  actionTimeFrom: string | null;
  actionTimeTo: string | null;
}

export type CompanyCfcType = typeof COMPANY_CFC[keyof typeof COMPANY_CFC];

export interface CustomerInfoForList {
  /**
   * 顧客ID
   */
  customerId: number;
  /**
   * お客さま番号
   */
  customerNumber: string;
  /**
   *  お客さま氏名
   */
  customerFullName: string;
  /**
   *  店舗名
   */
  ordcstmName: string;
  /**
   * 初回登録者
   */
  firstCreateUserName: string;
  /**
   * YMフラグ(企業区分)
   */
  ymFlg: CompanyCfcType;
  /**
   * 見積書番号保持有無
   * (料金シミュレーター連携済み判定)
   */
  hasCncgPrintNo: boolean;
  /**
   * 取り置き有無
   */
  hasKeep: boolean;
  /**
   * 商談情報リスト
   */
  negotiationList: NegotiationInfoOnCustomerInfoList[] | null;
  /**
   * 復活予定フラグ
   * 0 : 復活予定でない 1 : 復活予定
   */
  resurrectionFlg: string;

  /**
   * 復活予定日
   * resurrectionFlgが1でない場合はnull
   */
  resurrectionDate: string | null;
  /**
   * 作成日
   */
  createDate: string;
  /**
   * 更新日
   */
  latestUpdateDate: string;
  /**
   * 有効期限日
   */
  expirationDate: string;
  /**
   * 自店舗フラグ
   * 0 : 他店舗 1 : 自店舗
   */
  ownStoreFlg: string;
  /**
   * 家族構成その他(人数)
   */
  familyStructureOtherCount: NumberOrNull;
  /**
   * 商品情報
   */
  productInfo: ProductInfoOnCustomerInfo;
  /**
   * 直近のアクション情報
   * 項目自体がない場合はアクション自体が登録されていない事を示す
   */
  upcomingActionSchedule: ActionInfoOnCustomerInfo | null;
}

export interface CustomerListApiResponse {
  result: CommonResult;
  hit: number;
  customerResult: CustomerInfoForList[];
}

/**
 * 集約件数取得API
 * @param request 集約件数取得APIのリクエスト
 */
export async function customerListApi(request: CustomerListApiRequest): Promise<CustomerListApiResponse> {
  const headers = {
    'Content-Type': 'application/json',
  };

  try {
    const { data, status } = await axios.post<CustomerListApiResponse>(URL, request, { headers });
    if (status === OK.status) {
      return data;
    } else {
      throw new Error(data.result.resultCd);
    }
  } catch (error) {
    return {
      result: {
        jobDateTime: '',
        resultCd: '9999',
        resultMessageDtoList: [
          {
            resultMessageCd: '9999',
            resultMessage: `${API_EXCEPTION_MSG} エラーコード:${isAxiosError(error) ? error.message : '不明なエラー'}`,
          },
        ],
      },
      hit: 0,
      customerResult: [],
    };
  }
}
